// How the Inventory Overview toolbar is written into the URL, so a bookmark
// opens the same view. Each control's default leaves its param out. Values
// the page no longer knows (a deleted product group, a warehouse not on the
// page, a misspelled sort) are dropped.
import type { GroupByMode } from "./groupProducts";
import { UNASSIGNED } from "./productFilters";
import { isSortColumn, type MatrixSort } from "./stockHealth";

const LIST_SEPARATOR = ",";

function listFrom(raw: string | null): string[] {
  return (raw ?? "").split(LIST_SEPARATOR).filter((v) => v !== "");
}

// ?group=product-group; missing means ungrouped.
const PRODUCT_GROUP_PARAM = "product-group";

export function parseGroupBy(raw: string | null): GroupByMode {
  return raw === PRODUCT_GROUP_PARAM ? "product_group" : "none";
}

export function formatGroupBy(mode: GroupByMode): string | null {
  return mode === "product_group" ? PRODUCT_GROUP_PARAM : null;
}

// ?q=serum; missing means no search.
export function parseQuery(raw: string | null): string {
  return raw ?? "";
}

export function formatQuery(query: string): string | null {
  return query === "" ? null : query;
}

// ?groups=<id>,<id>,unassigned. Only ids in `known` are kept.
const UNASSIGNED_PARAM = "unassigned";

export function parseIdSet(raw: string | null, known: Set<string>): Set<string> {
  return new Set(listFrom(raw).map((v) => (v === UNASSIGNED_PARAM ? UNASSIGNED : v)).filter((v) => known.has(v)));
}

export function formatIdSet(ids: Set<string>): string | null {
  if (ids.size === 0) return null;
  return [...ids].map((v) => (v === UNASSIGNED ? UNASSIGNED_PARAM : v)).join(LIST_SEPARATOR);
}

// ?sort=doc, ?sort=doc-reversed, ?sort=none (sorting turned off). Missing or
// unknown means the default: Status in its normal order.
export const DEFAULT_SORT: NonNullable<MatrixSort> = { column: "status", direction: "normal" };
const REVERSED_SUFFIX = "-reversed";
const NO_SORT_PARAM = "none";

export function parseSort(raw: string | null): MatrixSort {
  if (raw === NO_SORT_PARAM) return null;
  if (raw === null) return DEFAULT_SORT;
  const reversed = raw.endsWith(REVERSED_SUFFIX);
  const column = reversed ? raw.slice(0, -REVERSED_SUFFIX.length) : raw;
  if (!isSortColumn(column)) return DEFAULT_SORT;
  return { column, direction: reversed ? "reversed" : "normal" };
}

export function formatSort(sort: MatrixSort): string | null {
  if (sort === null) return NO_SORT_PARAM;
  if (sort.column === DEFAULT_SORT.column && sort.direction === DEFAULT_SORT.direction) return null;
  return sort.direction === "reversed" ? `${sort.column}${REVERSED_SUFFIX}` : sort.column;
}
